import { Link, useParams } from "react-router-dom";
import { Phone } from "lucide-react";
import { trackCallClick } from "@/lib/googleAds";
import Nav from "@/components/Nav";
import ContactForm from "@/components/ContactForm";
import Footer from "@/components/Footer";
import { MaskedLine, Reveal } from "@/components/Reveal";
import { STATIC_PAGES } from "@/data/content";

const StaticPage = () => {
  const { pageId } = useParams();
  const page = STATIC_PAGES[pageId];

  if (!page) {
    return (
      <main data-testid="page-not-found">
        <Nav />
        <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
          <h1 className="font-serif-d text-5xl mb-6">Page not found.</h1>
          <Link to="/" data-testid="static-not-found-home-link" className="font-oswald text-sm tracking-[0.25em] uppercase border border-white/20 px-8 py-4 hover:border-[#0055FF] hover:text-[#0055FF] transition-colors duration-300">
            Back to the front page
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main data-testid={`static-page-${pageId}`}>
      <Nav />
      <header className="pt-40 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          <p className="font-mono-p text-xs tracking-[0.35em] uppercase text-[#0055FF] mb-6">{page.kicker}</p>
          <h1 className="font-serif-d text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight" data-testid="static-page-title">
            <MaskedLine delay={0.2}>{page.title}</MaskedLine>
          </h1>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 pb-28">
        {page.phone && (
          <>
            <Reveal className="mb-6">
            <a
              href={page.phoneHref}
              onClick={trackCallClick(page.phoneHref)}
              data-testid="contact-phone-cta"
              className="group flex flex-col sm:flex-row sm:items-center justify-between gap-6 border border-white/10 bg-[#121212]/40 p-8 md:p-10 glow-win transition-shadow duration-500"
            >
              <div>
                <span className="font-mono-p text-[10px] tracking-[0.3em] uppercase text-[#A1A1AA] block mb-3">
                  Call the editorial desk — Mon–Fri, 9am–6pm ET
                </span>
                <span className="font-oswald text-3xl md:text-4xl tracking-tight group-hover:text-[#0055FF] transition-colors duration-300">
                  {page.phone}
                </span>
              </div>
              <span className="flex items-center gap-3 font-oswald text-sm tracking-[0.25em] uppercase bg-[#F5F5F5] text-[#0A0A0A] px-8 py-4 group-hover:bg-[#0055FF] group-hover:text-white transition-colors duration-300 shrink-0">
                <Phone className="w-4 h-4" /> Call now
              </span>
            </a>
          </Reveal>
          <div className="mb-16">
            <ContactForm />
          </div>
          </>
        )}
        {page.sections.map((s, i) => (
          <Reveal key={i} className="mb-12 border-b border-white/10 pb-12 last:border-b-0">
            <h2 className="font-oswald text-xl md:text-2xl tracking-tight uppercase mb-5 flex items-baseline gap-4">
              <span className="text-sm text-stroke shrink-0">{String(i + 1).padStart(2, "0")}</span>
              {s.heading}
            </h2>
            <div className="prose-body font-mono-p text-sm md:text-base">
              <p>{s.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Footer />
    </main>
  );
};

export default StaticPage;
